import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Tabs, 
  Tab,
  Divider,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  limit,
  where
} from '../../firebase/firebase';
import { db } from '../../firebase/firebase';

// 产品管理组件
import ProductManagement from './ProductManagement';
// 订单管理组件
import OrderManagement from './OrderManagement';
// 用户管理组件
import UserManagement from './UserManagement';
// 设置组件
import SettingsManagement from './SettingsManagement';
// 占卜师管理组件
import FortunetellerManagement from './FortunetellerManagement';
// 客户消息管理组件
import MessageManagement from './MessageManagement';
// 评价管理组件
import ReviewManagement from './ReviewManagement';
// 预约管理组件
import BookingManagement from './BookingManagement';
// 文章管理组件
import ArticleManagement from './ArticleManagement';

const Admin = () => {
  const { currentUser, isAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  
  // 检查权限并重定向
  useEffect(() => {
    // 添加调试日志
    console.log("权限检查:", { currentUser: !!currentUser, isAdmin, loading });
  }, [currentUser, isAdmin, loading]);

  // 初始化空数据（如果需要）
  useEffect(() => {
    if (!dashboardData && !loadingData && !loading) {
      setDashboardData({
        userCount: 0,
        productCount: 0,
        orderCount: 0,
        fortuneTellerCount: 0,
        pendingOrderCount: 0,
        messageCount: 0,
        unreadMessageCount: 0,
        recentOrders: []
      });
    }
  }, [dashboardData, loadingData, loading]);
  
  // 如果用户无管理员权限，重定向到首页
  if (!loading && (!currentUser || !isAdmin)) {
    return <Navigate to="/login" replace />;
  }
  
  // 加载仪表盘数据
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoadingData(true);
        setError(null);
        
        // 获取用户总数
        let userCount = 0;
        let productCount = 0;
        let orderCount = 0;
        let fortuneTellerCount = 0;
        let recentOrders = [];
        let pendingOrderCount = 0;
        let messageCount = 0;
        let unreadMessageCount = 0;
        
        try {
          // 获取用户总数
          const usersRef = collection(db, "users");
          const usersSnapshot = await getDocs(usersRef);
          userCount = usersSnapshot.size;
        } catch (err) {
          console.log("无法获取用户数据，可能集合不存在:", err);
        }
        
        try {
          // 获取产品总数
          const productsRef = collection(db, "products");
          const productsSnapshot = await getDocs(productsRef);
          productCount = productsSnapshot.size;
        } catch (err) {
          console.log("无法获取产品数据，可能集合不存在:", err);
        }
        
        try {
          // 获取订单总数
          const ordersRef = collection(db, "orders");
          const ordersSnapshot = await getDocs(ordersRef);
          orderCount = ordersSnapshot.size;
          
          // 获取最近订单
          if (orderCount > 0) {
            const recentOrdersQuery = query(
              ordersRef, 
              orderBy("createdAt", "desc"), 
              limit(5)
            );
            const recentOrdersSnapshot = await getDocs(recentOrdersQuery);
            recentOrders = recentOrdersSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // 获取待处理订单数（已付款但尚未发货的订单）
            console.log("查询待处理订单(已付款未发货)...");
            const pendingOrdersQuery = query(
              ordersRef,
              where("status", "==", "paid") // 已付款
            );
            const pendingOrdersSnapshot = await getDocs(pendingOrdersQuery);
            pendingOrderCount = pendingOrdersSnapshot.size;
            
            // 打印查询到的待处理订单信息，以便调试
            console.log(`找到 ${pendingOrderCount} 个待处理订单(已付款未发货):`);
            pendingOrdersSnapshot.forEach(doc => {
              console.log(`待处理订单 ID: ${doc.id}, 状态: ${doc.data().status}, 创建时间: ${doc.data().createdAt?.toDate?.()}`);
            });
          }
        } catch (err) {
          console.log("无法获取订单数据，可能集合不存在:", err);
        }
        
        try {
          // 获取占卜师总数
          const fortuneTellersRef = collection(db, "fortuneTellers");
          const fortuneTellersSnapshot = await getDocs(fortuneTellersRef);
          fortuneTellerCount = fortuneTellersSnapshot.size;
        } catch (err) {
          console.log("无法获取占卜师数据，可能集合不存在:", err);
        }
        
        try {
          // 获取消息总数
          const mailRef = collection(db, "mail");
          const mailSnapshot = await getDocs(mailRef);
          messageCount = mailSnapshot.size;
          
          // 获取未读消息数
          const unreadMailQuery = query(
            mailRef,
            where("read", "==", false)
          );
          const unreadMailSnapshot = await getDocs(unreadMailQuery);
          unreadMessageCount = unreadMailSnapshot.size;
        } catch (err) {
          console.log("无法获取消息数据，可能集合不存在:", err);
        }
        
        // 设置仪表盘数据
        setDashboardData({
          userCount,
          productCount,
          orderCount,
          fortuneTellerCount,
          pendingOrderCount,
          messageCount,
          unreadMessageCount,
          recentOrders
        });
      } catch (err) {
        console.error("获取仪表盘数据失败:", err);
        setError("获取仪表盘数据失败");
      } finally {
        setLoadingData(false);
      }
    };
    
    if (currentUser && isAdmin) {
      fetchDashboardData();
    }
  }, [currentUser, isAdmin, db]);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // 渲染加载指示器 - 合并所有加载状态条件
  if (loading || loadingData || !dashboardData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          管理后台
        </Typography>
        <Typography variant="body1" color="text.secondary">
          欢迎回来，管理员。在这里您可以管理产品、订单、用户和系统设置。
        </Typography>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}
      
      {/* 仪表盘卡片 */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Paper elevation={1} sx={{ p: 3, flex: '1 0 200px' }}>
          <Typography variant="h6" gutterBottom>用户总数</Typography>
          <Typography variant="h3">{dashboardData.userCount}</Typography>
        </Paper>
        
        <Paper elevation={1} sx={{ p: 3, flex: '1 0 200px' }}>
          <Typography variant="h6" gutterBottom>产品总数</Typography>
          <Typography variant="h3">{dashboardData.productCount}</Typography>
        </Paper>
        
        <Paper elevation={1} sx={{ p: 3, flex: '1 0 200px' }}>
          <Typography variant="h6" gutterBottom>订单总数</Typography>
          <Typography variant="h3">{dashboardData.orderCount}</Typography>
        </Paper>
        
        <Paper elevation={1} sx={{ p: 3, flex: '1 0 200px' }}>
          <Typography variant="h6" gutterBottom>占卜师总数</Typography>
          <Typography variant="h3">{dashboardData.fortuneTellerCount}</Typography>
        </Paper>
        
        <Paper elevation={1} sx={{ p: 3, flex: '1 0 200px' }}>
          <Typography variant="h6" gutterBottom>待处理订单</Typography>
          <Typography variant="h3">{dashboardData.pendingOrderCount}</Typography>
        </Paper>
        
        <Paper elevation={1} sx={{ p: 3, flex: '1 0 200px' }}>
          <Typography variant="h6" gutterBottom>客户消息</Typography>
          <Typography variant="h3">{dashboardData.messageCount}</Typography>
          {dashboardData.unreadMessageCount > 0 && (
            <Typography variant="subtitle1" color="primary">
              {dashboardData.unreadMessageCount} 条未读
            </Typography>
          )}
        </Paper>
      </Box>
      
      {/* 管理选项卡 */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="产品管理" />
          <Tab label="订单管理" />
          <Tab label="预约管理" />
          <Tab label="用户管理" />
          <Tab label="占卜师管理" />
          <Tab label="评价管理" />
          <Tab label="消息管理" />
          <Tab label="系统设置" />
          <Tab label="文章管理" />
        </Tabs>
        
        <Divider />
        
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom>产品管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此管理您的水晶产品、添加新产品、编辑现有产品或将产品下架。
              </Typography>
              <ProductManagement />
            </Box>
          )}
          
          {activeTab === 1 && (
            <Box>
              <Typography variant="h5" gutterBottom>订单管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此查看并处理所有订单，更新订单状态，查看订单详情。
              </Typography>
              <OrderManagement />
            </Box>
          )}
          
          {activeTab === 2 && (
            <Box>
              <Typography variant="h5" gutterBottom>预约管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此查看并处理所有占卜师预约，更新预约状态，处理支付状态。
              </Typography>
              <BookingManagement />
            </Box>
          )}
          
          {activeTab === 3 && (
            <Box>
              <Typography variant="h5" gutterBottom>用户管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此查看并管理用户账户，编辑用户信息，重置密码或禁用账户。
              </Typography>
              <UserManagement />
            </Box>
          )}
          
          {activeTab === 4 && (
            <Box>
              <Typography variant="h5" gutterBottom>占卜师管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此管理命理师信息，添加新命理师，编辑现有命理师资料或管理评价。
              </Typography>
              <FortunetellerManagement />
            </Box>
          )}
          
          {activeTab === 5 && (
            <Box>
              <Typography variant="h5" gutterBottom>评价管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此管理用户对产品和命理师的评价，审核、回复或隐藏不当评价。
              </Typography>
              <ReviewManagement />
            </Box>
          )}
          
          {activeTab === 6 && (
            <Box>
              <Typography variant="h5" gutterBottom>消息管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此管理客户发送的消息，查看、回复和标记消息状态。
              </Typography>
              <MessageManagement />
            </Box>
          )}
          
          {activeTab === 7 && (
            <Box>
              <Typography variant="h5" gutterBottom>系统设置</Typography>
              <Typography color="text.secondary" paragraph>
                在此配置系统参数，设置API密钥和其他全局选项。
              </Typography>
              <SettingsManagement />
            </Box>
          )}
          
          {activeTab === 8 && (
            <Box>
              <Typography variant="h5" gutterBottom>文章管理</Typography>
              <Typography color="text.secondary" paragraph>
                在此管理SEO文章，查看文章列表，手动触发新文章生成，或删除无用文章。
              </Typography>
              <ArticleManagement />
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Admin;